export type AcademySetupItemKey =
  | "facility_profile"
  | "calendar_options"
  | "booking_options"
  | "stripe_connect"
  | "packages_subscriptions"
  | "coach_profile"
  | "academy_page"
  | "invite_athlete"
  | "mobile_app";

export type AcademySetupPhaseKey = "schedule" | "get_paid" | "get_customers";

/** Per-item completion derived from the API by the academySetup store slice. */
export type AcademySetupCompletion = Record<AcademySetupItemKey, boolean>;

/**
 * What the coach did about an optional item, persisted by the BE per coach:
 * "visited" = opened the page from the checklist, "skipped" = clicked
 * "Skip for now". Either one completes the item for progress (product rule
 * 2026-09-02) while the row keeps saying "Not set up yet" / "Skipped for now"
 * until the API flag is true; required items complete only through their API
 * flag. The pair is
 * also the funnel signal product correlates against the real flag later.
 */
export type AcademySetupOptionalState = "visited" | "skipped";

export type AcademySetupOptionalStates = Partial<
  Record<AcademySetupItemKey, AcademySetupOptionalState>
>;

export interface AcademySetupItem {
  key: AcademySetupItemKey;
  phase: AcademySetupPhaseKey;
  titleKey: string;
  /** Second line while the item is incomplete; completed rows show none. */
  subtitleKey?: string;
  actionKey: string;
  /**
   * Past-tense label once the item is genuinely done (API flag true).
   * Absent where the imperative already reads as done ("Set up") or the row
   * stays actionable ("Edit").
   */
  completeActionKey?: string;
  /** Keeps the action clickable after completion (the row's design has an Edit affordance). */
  staysActionable?: boolean;
  /**
   * Counts as complete on a checklist visit or "Skip for now", not only
   * through the API flag; the row offers the skip link while untouched.
   */
  optional?: boolean;
  path: string;
  search?: string;
  /** Counts toward progress: done, or (optional) visited/skipped. */
  complete: boolean;
  /** The API flag itself — the thing is actually set up. */
  done: boolean;
  /**
   * How an optional item got complete without being done: the row reads
   * "Not set up yet" after a visit and "Skipped for now" after a skip.
   */
  optionalState?: AcademySetupOptionalState;
  /** Only set for items whose popup exists in the design; others show none. */
  hintTitleKey?: string;
  hintBodyKey?: string;
}

export interface AcademySetupHintState {
  academySetupHint?: AcademySetupItemKey;
}

/** Router state the entry points push to /academy/setup so setup_page_viewed can name them. */
export type AcademySetupEntryPoint = "sidebar_pill" | "wizard_complete" | "rail";

export interface AcademySetupLocationState {
  setupEntryPoint?: AcademySetupEntryPoint;
}

/** Which CTA launched an item, for the completion event's completion_source. */
export type AcademySetupCtaSource = "checklist_cta" | "rail_cta";
/** completion_source as reported: a CTA, or none on record (organic). */
export type AcademySetupCompletionSource = AcademySetupCtaSource | "organic_nav";

export interface AcademySetupCta {
  key: AcademySetupItemKey;
  source: AcademySetupCtaSource;
  /** Date.now() at the click, so the completion can report time_to_complete_ms. */
  at: number;
}

/** Spec v2 §3 phase order; grouping communicates recommendation, not enforcement. */
export const academySetupPhases: Array<{
  key: AcademySetupPhaseKey;
  titleKey: string;
}> = [
  { key: "schedule", titleKey: "Setup your schedule" },
  { key: "get_paid", titleKey: "Get paid" },
  { key: "get_customers", titleKey: "Get customers" },
];

export const academySetupItemConfigs: Array<
  Omit<AcademySetupItem, "complete" | "done">
> =
  [
    {
      key: "facility_profile",
      phase: "schedule",
      titleKey: "Facility profile created",
      actionKey: "Edit",
      staysActionable: true,
      path: "/manage_facility",
      // No coachmark frame exists for this item (product ask 2026-09-04);
      // anchored to the Facility card's Edit button on Admin Settings → General.
      // Swap the copy if design publishes dedicated text.
      hintTitleKey: "Facility profile",
      hintBodyKey:
        "Edit your facility's name and address to complete your facility profile.",
    },
    {
      key: "calendar_options",
      phase: "schedule",
      titleKey: "Set your availability and calendar options",
      subtitleKey: "Availability, schedule options, and calendar integrations",
      actionKey: "Set up",
      path: "/calendar/availability",
      // Figma 531:10055 — anchored to the first schedule's Thu day button.
      hintTitleKey: "Set your availability",
      hintBodyKey:
        "Choose the days and times when athletes can book sessions with you.",
    },
    {
      key: "booking_options",
      phase: "schedule",
      titleKey: "Create your booking options",
      subtitleKey: "Define and configure the services you offer to athletes.",
      actionKey: "Create",
      completeActionKey: "Created",
      path: "/calendar/pricing",
      hintTitleKey: "Calendar & Booking",
      hintBodyKey:
        "Create your booking options, set your weekly availability, configure scheduling rules, and connect your external calendars.",
    },
    {
      key: "stripe_connect",
      phase: "get_paid",
      titleKey: "Connect Stripe to accept online payments",
      subtitleKey:
        "This gives your athletes the option to pay for your services directly in CoachNow",
      actionKey: "Connect",
      completeActionKey: "Connected",
      optional: true,
      path: "/billing",
      search: "?tab=setup",
      hintTitleKey: "Billing & Payments",
      hintBodyKey:
        "Connect Stripe to accept online payments for sessions, packages, and subscriptions.",
    },
    {
      key: "packages_subscriptions",
      phase: "get_paid",
      titleKey: "Create packages and subscriptions",
      subtitleKey:
        "Sell your services via one-time payments or recurring subscriptions.",
      actionKey: "Create",
      completeActionKey: "Created",
      optional: true,
      path: "/billing",
    },
    {
      key: "coach_profile",
      phase: "get_customers",
      titleKey: "Complete and enable your coach profile",
      subtitleKey:
        "This is the page where athletes can book sessions and buy packages.",
      actionKey: "Complete",
      completeActionKey: "Completed",
      path: "/account/coach-profile",
      hintTitleKey: "Coach Profile",
      hintBodyKey:
        "Turn this on to make your public page visible to athletes, so they can book sessions and purchase packages.",
    },
    {
      key: "academy_page",
      phase: "get_customers",
      titleKey: "Create your academy's public page",
      subtitleKey:
        "Showcases all your coaches, with a link to each coach's booking page",
      actionKey: "Create",
      completeActionKey: "Created",
      optional: true,
      path: "/manage_facility/landing_page",
      hintTitleKey: "Facility Landing Page",
      hintBodyKey:
        "Fill out and save this page, then enable Public Facility Landing Page in Admin Settings → General.",
    },
    {
      key: "invite_athlete",
      phase: "get_customers",
      titleKey: "Invite your first athlete",
      subtitleKey:
        "Already have athletes you'd like to add to CoachNow? You can invite them via email here.",
      actionKey: "Invite",
      completeActionKey: "Invited",
      // Lands on the Spaces list; the coachmark points at its "+ New Space".
      path: "/spaces",
      hintTitleKey: "Invite your first athlete",
      hintBodyKey:
        "Invite an athlete via email and create a personal Space to start working together.",
    },
    {
      key: "mobile_app",
      phase: "get_customers",
      titleKey: "Get the CoachNow app",
      subtitleKey:
        "Download the iOS or Android app to access the video analysis suite and message your athletes on the go.",
      actionKey: "Get App",
      // The flag behind this row is mobile_app_installed, so "Installed" is
      // the truthful past tense of "Get App".
      completeActionKey: "Installed",
      // No destination page: the row opens the app-download modal in place.
      path: "/academy/setup",
    },
  ];

/**
 * null (nothing fetched yet) renders every item as incomplete. Optional
 * items also count as complete through their persisted visited/skipped
 * state; `done` stays the raw API flag so the row can still say the thing
 * isn't set up and keep its action live.
 */
export const withCompletion = (
  completion: AcademySetupCompletion | null,
  optionalStates: AcademySetupOptionalStates = {},
): AcademySetupItem[] => {
  return academySetupItemConfigs.map((item) => {
    const done = Boolean(completion && completion[item.key]);
    const state = item.optional ? optionalStates[item.key] : undefined;
    return {
      ...item,
      complete: done || Boolean(state),
      done,
      optionalState: state,
    };
  });
};

const stripeItem = (items: AcademySetupItem[]): AcademySetupItem | undefined =>
  items.find((item) => item.key === "stripe_connect");

/**
 * Whether confirming Offline Only in the guard records the coach's decision
 * not to use online payments as the Stripe item's skip: only without Stripe,
 * and only while the item is not already connected or skipped. A visited row
 * is overwritten — a visit made no choice, the confirmation does (product
 * decision 2026-09-16). `null` = checklist not loaded: the mark is still
 * written, since Stripe being unconnected is known from the profile, the PUT
 * is idempotent, and a local mark survives the fetch that lands later.
 */
export const offlineChoiceSkipsStripe = (
  items: AcademySetupItem[] | null,
  stripeConnected: boolean,
): boolean => {
  if (stripeConnected) {
    return false;
  }
  if (!items) {
    return true;
  }
  const stripe = stripeItem(items);
  return Boolean(
    stripe && !stripe.done && stripe.optionalState !== "skipped",
  );
};

/**
 * The guard's choice as spec §6.2's collection method: Offline Only is
 * collecting in person, Online Payments is collecting through CoachNow. The
 * guard is single-select, so "both" never arises here.
 */
export const billingMethodOf = (
  choice: "online" | "offline",
): "in_person" | "coachnow" => (choice === "offline" ? "in_person" : "coachnow");

/**
 * Where the billing choice was made, by the same rule that attributes item
 * completions: a checklist or rail CTA for a Billing item (Connect Stripe,
 * or Create packages) still on record means the checklist sent the coach
 * here; otherwise the choice was made from Billing itself.
 */
export const billingMethodSource = (
  ctas: Partial<Record<AcademySetupItemKey, AcademySetupCta>>,
): "checklist" | "settings" =>
  ctas.packages_subscriptions || ctas.stripe_connect ? "checklist" : "settings";

export const completedCount = (items: AcademySetupItem[]): number => {
  return items.filter((item) => item.complete).length;
};

export const allComplete = (items: AcademySetupItem[]): boolean => {
  return items.every((item) => item.complete);
};
